/**
 * GPU hosts API client.
 *
 * Mirrors POST/GET/PUT/DELETE /admin/gpu/hosts on tacosai-admin-api.
 * Surfaces every column on omega_engine.registered_hosts — including the
 * bridge-transport columns (r2_output_prefix / auth_scheme / auth_secret_ref)
 * added by ops/sql/13-bridge-host-columns.sql. The admin UI is the only
 * operational interface for these; no hidden columns.
 */

import { request } from './client.js';

export type HostStatus = 'active' | 'drain' | 'disabled' | 'maintenance';
export type AuthScheme = 'service_token' | 'cf_access' | 'mtls';

export interface GpuHost {
  id: string;
  host_url: string;
  datacenter: string;
  capability_set: string[];
  weight: number;
  status: HostStatus;
  mtls_cert_id: string | null;
  last_health_at: string | null;
  last_health_ok: boolean | null;
  notes: string | null;
  r2_output_prefix: string | null;
  auth_scheme: AuthScheme;
  auth_secret_ref: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateGpuHostInput {
  host_url: string;
  datacenter: string;
  capability_set: string[];
  weight?: number;
  status?: HostStatus;
  mtls_cert_id?: string | null;
  auth_token?: string | null;
  notes?: string | null;
  r2_output_prefix?: string | null;
  auth_scheme?: AuthScheme;
  auth_secret_ref?: string | null;
}

export type UpdateGpuHostInput = Partial<Omit<CreateGpuHostInput, 'auth_token'>>;

export interface ListGpuHostsQuery {
  status?: HostStatus;
  datacenter?: string;
  auth_scheme?: AuthScheme;
}

export async function listGpuHosts(query: ListGpuHostsQuery = {}): Promise<{ items: GpuHost[] }> {
  return request<{ items: GpuHost[] }>({
    method: 'GET',
    url: '/admin/gpu/hosts',
    query: query as Record<string, string | undefined>,
  });
}

export async function getGpuHost(id: string): Promise<GpuHost> {
  return request<GpuHost>({ method: 'GET', url: `/admin/gpu/hosts/${id}` });
}

export async function createGpuHost(input: CreateGpuHostInput): Promise<GpuHost> {
  return request<GpuHost>({ method: 'POST', url: '/admin/gpu/hosts', body: input });
}

export async function updateGpuHost(id: string, input: UpdateGpuHostInput): Promise<GpuHost> {
  return request<GpuHost>({ method: 'PUT', url: `/admin/gpu/hosts/${id}`, body: input });
}

export async function deleteGpuHost(id: string): Promise<void> {
  await request<void>({ method: 'DELETE', url: `/admin/gpu/hosts/${id}` });
}
